import React, { useState, useEffect } from 'react';
import { Users, Mail, Phone, Check, X, Clock, Trash2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminVolunteers() {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchVolunteers(); }, []);

  const fetchVolunteers = async () => {
    try {
      const res = await axios.get(`${API}/volunteers`, { withCredentials: true });
      setVolunteers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/volunteers/${id}`, { status }, { withCredentials: true });
      fetchVolunteers();
      toast.success(`Status updated to ${status}`);
    } catch (e) { toast.error('Failed to update'); }
  };

  const deleteVolunteer = async (id) => {
    if (!window.confirm('Delete this volunteer application?')) return;
    try {
      await axios.delete(`${API}/volunteers/${id}`, { withCredentials: true });
      toast.success('Application deleted');
      fetchVolunteers();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const statusColors = {
    pending: 'bg-gold/10 text-gold',
    contacted: 'bg-blue-100 text-blue-600',
    approved: 'bg-olive/10 text-olive',
    denied: 'bg-red-100 text-red-600'
  };

  return (
    <div data-testid="admin-volunteers">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal">Volunteers</h1>
        <p className="text-charcoal/60 mt-1">Manage volunteer applications</p>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : volunteers.length > 0 ? (
        <div className="space-y-4">
          {volunteers.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-lg text-charcoal">{v.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[v.status] || statusColors.pending}`}>
                      {v.status || 'pending'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-charcoal/60">
                    <span className="flex items-center gap-1"><Mail size={14} /> {v.email}</span>
                    <span className="flex items-center gap-1"><Phone size={14} /> {v.phone}</span>
                  </div>
                  {v.interests?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {v.interests.map((i, idx) => (
                        <span key={idx} className="px-2 py-1 bg-cream text-xs text-charcoal/70 rounded">{i}</span>
                      ))}
                    </div>
                  )}
                  {v.availability && <p className="text-sm text-charcoal/50 mt-2">Availability: {v.availability}</p>}
                  {v.message && <p className="text-sm text-charcoal/60 mt-2 italic">"{v.message}"</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => updateStatus(v.id, 'contacted')} className="text-blue-600 border-blue-200">
                    <Clock size={14} className="mr-1" /> Contacted
                  </Button>
                  <Button size="sm" onClick={() => updateStatus(v.id, 'approved')} className="bg-olive text-white hover:bg-olive/80">
                    <Check size={14} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(v.id, 'denied')} className="text-red-600 border-red-200">
                    <X size={14} className="mr-1" /> Deny
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteVolunteer(v.id)} className="text-red-600 border-red-200">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <Users size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/50">No volunteer applications yet</p>
        </div>
      )}
    </div>
  );
}
